import enCommon from "./en/common.json";
import enDocs from "./en/docs.json";
import enIam from "./en/iam.json";
import enLayout from "./en/layout.json";
import frCommon from "./fr/common.json";
import frIam from "./fr/iam.json";
import frLayout from "./fr/layout.json";

/** DEFAULT_NAMESPACE is the namespace used when none is specified. */
export const DEFAULT_NAMESPACE = "common";

/**
 * NAMESPACES are the per-domain i18n namespaces shared by web and docs: common
 * (app-wide), layout (shell/nav), and iam (the auth flows plus the
 * account/organization chrome — auth is part of identity & access management).
 *
 * The studio is not among them and cannot come back. Its twelve hundred sentences travel with
 * the package a consumer installs — `@mountaya/studio/strings` — because a studio that
 * needed this application's translation runtime would show a licensee its own key names. This
 * application reads that same bundle back through `useStudioStrings()`, so there is one
 * declaration, and en/fr parity for it is a COMPILE error at the package rather than a runtime
 * check here.
 */
export const NAMESPACES = ["common", "layout", "iam"] as const;

/**
 * DOCS_NAMESPACE is the developer-documentation chrome namespace. It is
 * English-only by design and deliberately kept out of NAMESPACES, so it never
 * enters the en/fr key-parity gate; only the docs app loads it (via
 * createDocsI18n). A second language can be added purely additively later by
 * supplying a docs bundle under that language in resources.
 */
export const DOCS_NAMESPACE = "docs";

/**
 * SUPPORTED_LANGUAGES are the UI languages. The documentation namespace stays
 * English-only by design (the docs app inherits this exception); it is excluded
 * from the en/fr key-parity gate.
 */
export const SUPPORTED_LANGUAGES = ["en", "fr"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * DEFAULT_LANGUAGE is the language used when none is resolved from context: the
 * first supported language. It is the intentional default the docs root redirect
 * and language resolution fall back to, rather than whichever language happens to
 * sort or load first.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = SUPPORTED_LANGUAGES[0];

/**
 * resources is the i18next resource tree. Every key under en/ must also exist under fr/
 * (enforced by the parity test), excluding the documentation namespace. docs is English-only and
 * ships under en only; a second language is added by supplying its docs bundle here, with no
 * other change required.
 *
 * What is here is this application's own chrome — the account pages, the nav, the shared words.
 * Everything the studio says lives in the package that renders it; see {@link NAMESPACES}.
 */
export const resources = {
  en: {
    common: enCommon,
    docs: enDocs,
    iam: enIam,
    layout: enLayout,
  },
  fr: { common: frCommon, iam: frIam, layout: frLayout },
} as const;
